import { useState, useCallback } from "react";
import PropTypes from "prop-types";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { Modal } from "@/config/antd";
import { ArrowRightOutlined } from "@/config/antd";
import {
  loginAsync,
  selectError,
  clearError,
} from "@/store/redux/slices/auth/auth_slice";
import Input from "@/components/common/inputs/Input";
import Button from "@/components/common/Button";
import WebIcon from "@/components/common/Web_Icons";
import Error from "@/components/common/alerts/Error";
import { useLoader } from "@/hooks/LoaderProvider";
import { getLoginErrorContent } from "@/helper/error_handling";
import DynamicPollsGoogleButton from "@/pages/admin/polls/Dynamic_Polls_Google_Button";

const DynamicPollsLoginModal = ({
  isVisible,
  onDismiss,
  onLoginFailed,
  onProceedAnonymously,
}) => {
  const dispatch = useDispatch();
  const { showLoader, hideLoader } = useLoader();
  const loginError = useSelector(selectError);

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isErrorVisible, setIsErrorVisible] = useState(false);

  const isFormValid = email.trim() !== "" && password.trim() !== "";

  const handleProceed = useCallback(async () => {
    if (!isFormValid) return;

    // Close the modal immediately when Proceed is clicked
    onDismiss?.();

    showLoader();
    dispatch(clearError());

    try {
      await dispatch(
        loginAsync({ email: email.trim(), password })
      ).unwrap();

      setEmail("");
      setPassword("");
    } catch {
      setIsErrorVisible(true);
      onLoginFailed?.();
    } finally {
      hideLoader();
    }
  }, [
    dispatch,
    email,
    password,
    hideLoader,
    isFormValid,
    onDismiss,
    onLoginFailed,
    showLoader,
  ]);

  const handleCloseError = () => {
    setIsErrorVisible(false);
    dispatch(clearError());
  };

  const handleGoogleClick = () => {
    onDismiss?.();
  };

  const handleGoogleSuccess = () => {
  };

  const handleGoogleError = () => {
    onLoginFailed?.();
  };

  const errorContent = getLoginErrorContent(loginError);

  return (
    <>
      <Modal
        open={isVisible}
        onCancel={null}   
        footer={null}
        centered
        width={480}
        closeIcon={null}
        maskClosable={false}
        keyboard={false}
        styles={{
          body: { padding: 0 },
          content: {
            padding: 0,
            borderRadius: "16px",
            overflow: "hidden",
          },
          mask: { backgroundColor: "rgba(0, 0, 0, 0.75)" },
        }}>
        <div className="flex flex-col bg-white text-left p-6">
          <h2 className="text-primary font-bold text-xl mb-2">
            Log into your account
          </h2>
          <p className="text-darkGrey text-small mb-6">
            Please log into your account to answer this survey. But you can
            answer this anonymously if that is okay with you.
          </p>

          <div className="space-y-4 mb-4">
            <Input
              label="EMAIL ADDRESS"
              placeholder="email@domain.com"
              icon={<WebIcon icon="Message" size="small" />}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              id="dynamic-polls-email"
            />
            <Input
              label="PASSWORD"
              placeholder="Enter your password"
              icon={<WebIcon icon="Lock" size="small" />}
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              id="dynamic-polls-password"
            />
          </div>

          <Button
            text="Proceed"
            icon={<ArrowRightOutlined />}
            onClick={handleProceed}
            disabled={!isFormValid}
            isActive={isFormValid}
            color="primary"
            className="w-full"
          />

          <div className="flex flex-row flex-wrap items-center justify-center gap-4 mt-5">
            <Link
              to="/signup"
              className="font-medium underline text-primary text-small">
              Create account
            </Link>
            <button
              type="button"
              onClick={onProceedAnonymously}
              className="font-medium underline text-primary text-small bg-transparent border-0 cursor-pointer p-0">
              Proceed anonymously
            </button>
            <Link
              to="/account-recovery/password-recovery"
              className="font-medium underline text-primary text-small">
              Forgot password
            </Link>
          </div>

          <p className="text-black text-small font-medium text-center mt-6 mb-4">
            or use our social media account
          </p>

          <DynamicPollsGoogleButton
            onClick={handleGoogleClick}
            onSuccess={handleGoogleSuccess}
            onError={handleGoogleError}
          />
        </div>
      </Modal>

      <Error
        isVisible={isErrorVisible}
        onClose={handleCloseError}
        header={errorContent.header}
        paragraph={errorContent.paragraph}
        buttonText={errorContent.buttonText}
        recoveryPath={errorContent.recoveryPath}
      />
    </>
  );
};

DynamicPollsLoginModal.propTypes = {
  isVisible: PropTypes.bool.isRequired,
  onDismiss: PropTypes.func,
  onLoginFailed: PropTypes.func,
  onProceedAnonymously: PropTypes.func,
};

export default DynamicPollsLoginModal;
